import { useEffect, useState } from 'react';
import { listDevices, listPairings, type DeviceRow, type PairingRow } from '../api';
import { FamilyFilter, NO_FAMILY, byFamily, serverFamily } from '../lib/family';
import { Pager, ScanNote, useCursorList } from '../lib/paging';
import { replace } from '../lib/route';
import {
  Empty,
  Id,
  Link,
  LocalFilterNote,
  NotBuilt,
  Pill,
  Result,
  Toolbar,
  ago,
  when,
} from '../lib/ui';

/**
 * Who can see this family's films.
 *
 * A paired child device holds a bearer token with no expiry, so this list is the
 * answer to a question support gets and could not previously answer at all:
 * "which of my eight devices is which, and is there one I don't recognise". All
 * eight live devices are on one family.
 *
 * The token is never rendered, in any mode — not even a prefix. The API does not
 * return it (it lives on the byToken GSI and whoever holds it can watch that
 * family's library), and a partial secret is still a secret with fewer characters
 * to guess. If a `deviceToken` ever does appear in a response, this screen raises
 * an alarm rather than displaying it, because that would be a leak worth
 * interrupting somebody's afternoon for.
 */
export function Devices({ initialFamily }: { initialFamily?: string }) {
  const [family, setFamily] = useState(initialFamily ?? '');

  useEffect(() => {
    replace({ name: 'devices', ...(family ? { familyId: family } : {}) });
  }, [family]);

  const familyParam = serverFamily(family);
  const list = useCursorList<DeviceRow>(
    (cursor) =>
      listDevices({
        ...(familyParam ? { family: familyParam } : {}),
        ...(cursor ? { next: cursor } : {}),
      }),
    [familyParam ?? ''],
  );

  return (
    <>
      <Toolbar>
        <FamilyFilter value={family} onChange={setFamily} />
      </Toolbar>

      <Result of={list.result} what="devices" retry={list.reload}>
        {(page) => {
          const rows = family === NO_FAMILY ? byFamily(page.rows, family) : page.rows;
          const leaked = page.rows.filter((row) =>
            Object.prototype.hasOwnProperty.call(row, 'deviceToken'),
          );
          return (
            <>
              {leaked.length > 0 && (
                <div className="notice notice-bad">
                  <b>The API returned a deviceToken. It should not.</b>
                  <span className="muted small">
                    {leaked.length} row{leaked.length === 1 ? '' : 's'} carried a
                    bearer credential in the response body. Not rendered here.
                    This is a control-plane leak — treat the affected devices as
                    compromised and get the field removed from the handler.
                  </span>
                </div>
              )}
              <Toolbar>
                <Pager list={list} what="devices" shown={rows.length} />
              </Toolbar>
              <LocalFilterNote
                active={family === NO_FAMILY}
                what="devices"
                why="No endpoint has a query for 'rows with no familyId'."
              />
              {!rows.length ? (
                <Empty what="devices" filtered={Boolean(family)} />
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Family</th>
                      <th>Can it spend?</th>
                      <th>Paired</th>
                      <th>Last seen</th>
                      <th>Token</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((device) => (
                      <tr key={device.deviceId}>
                        <td>
                          {device.deviceName || <span className="muted">unnamed</span>}
                          <div>
                            <Id value={device.deviceId} chars={12} />
                          </div>
                        </td>
                        <td>
                          {device.familyId ? (
                            <Link to={{ name: 'families', familyId: device.familyId }}>
                              {device.familyName || <Id value={device.familyId} />}
                            </Link>
                          ) : (
                            <Pill tone="bad">no family</Pill>
                          )}
                        </td>
                        <td>
                          {device.childOnly ? (
                            <Pill tone="on" title="reaches /watch/* only — no spending, no approval">
                              watch only
                            </Pill>
                          ) : (
                            <Pill tone="warn" title="a parent device: it can commission films and approve them">
                              full access
                            </Pill>
                          )}
                        </td>
                        <td className="muted nowrap">{when(device.pairedAt ?? device.createdAt)}</td>
                        <td className="muted nowrap">
                          {device.lastSeenAt ? ago(device.lastSeenAt) : <span className="muted">never</span>}
                        </td>
                        <td>
                          {device.hasToken ? (
                            <Pill title="a bearer token exists on this row and is never returned by the control plane">
                              present
                            </Pill>
                          ) : (
                            <span className="muted">none</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <ScanNote listing={page} />
              <NotBuilt
                what="Revoking a device from here"
                detail="DELETE /devices/{deviceId} exists on the FAMILY API, behind the parent's own login, and that is where it stays. A staff token that can unpair a child's iPad is new power, and it would sit behind whatever the tenant's conditional-access policy happens to be rather than behind anything this product controls."
              />
            </>
          );
        }}
      </Result>

      <Pairings family={familyParam} />
    </>
  );
}

/**
 * Codes in flight. Normally an empty table, and that is the healthy state.
 *
 * The code IS the credential — `POST /pairings/{code}/claim` is deliberately an
 * open route, because the claiming device has nothing else to authenticate with —
 * so the API masks them. Enough survives to match a code a parent reads out on a
 * support call; not enough to redeem one.
 */
function Pairings({ family }: { family?: string }) {
  const list = useCursorList<PairingRow>(
    (cursor) =>
      listPairings({
        ...(family ? { family } : {}),
        ...(cursor ? { next: cursor } : {}),
      }),
    [family ?? ''],
  );

  return (
    <section className="detail">
      <h2>Pairing codes in flight</h2>
      <Result
        of={list.result}
        what="pairing codes"
        retry={list.reload}
        missing="This build of the staff API has no /admin/pairings route."
      >
        {(page) =>
          !page.rows.length ? (
            <>
              <Empty what="codes in flight" />
              <p className="muted small">
                The expected state. A pairing code lives five minutes and TTL is
                enabled on this table, so it sweeps itself.
              </p>
            </>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Family</th>
                  <th>Grants</th>
                  <th>Created</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((pairing) => (
                  <tr key={pairing.code}>
                    <td className="mono">{pairing.code}</td>
                    <td>
                      {pairing.familyId ? (
                        <Link to={{ name: 'families', familyId: pairing.familyId }}>
                          {pairing.familyName || <Id value={pairing.familyId} />}
                        </Link>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="muted small">
                      {pairing.childOnly ? 'watch only' : 'full access'}
                    </td>
                    <td className="muted nowrap">{when(pairing.createdAt)}</td>
                    <td className="muted nowrap">
                      {pairing.expired ? (
                        <Pill tone="warn" title="devices.py enforces expiry on read too, so this is already unusable — it is waiting on the TTL sweep">
                          expired
                        </Pill>
                      ) : (
                        when(pairing.expiresAt)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        }
      </Result>
      <p className="muted small">
        Codes are masked by the API. A pairing code is a bearer credential, and a
        staff page showing live ones is a page that can join a device to somebody
        else's household.
      </p>
    </section>
  );
}
